// When importing this file from a .js file, always include the .ts extension in the import statement.
// Example: import { useMeetings } from '../hooks/useMeetings.ts';
// This prevents module resolution errors in mixed JS/TS codebases.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface Meeting {
  id: number;
  title: string;
  description?: string;
  meeting_date: string;
  start_time: string;
  end_time?: string;
  location?: string;
  agenda?: string;
  status: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  type?: string;
  created_at: string;
  updated_at: string;
  attendances_count: number;
  attendances?: MeetingAttendance[];
  creator?: {
    id: number;
    name: string;
  };
  attendance_stats?: {
    total: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
    attendance_rate: number;
  };
}

export interface MeetingAttendance {
  id: number;
  meeting_id: number;
  member_id: number;
  status: 'present' | 'absent' | 'late' | 'excused' | 'accepted' | 'declined';
  arrival_time?: string;
  departure_time?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  member: {
    id: number;
    name: string;
    phone_number: string;
  };
}

export interface CreateMeetingData {
  title: string;
  description?: string;
  meeting_date: string;
  start_time: string;
  location?: string;
  agenda?: string;
  type: string;
}

export interface UpdateMeetingData {
  title: string;
  description?: string;
  meeting_date: string;
  start_time: string;
  location?: string;
  agenda?: string;
  status?: 'scheduled' | 'ongoing' | 'completed' | 'cancelled';
  type: string;
  send_cancel_sms: boolean;
  send_reschedule_sms: boolean
}

export interface CreateAttendanceData {
  meeting_id: number;
  member_id: number;
  status: 'present' | 'absent' | 'late' | 'excused';
  arrival_time?: string;
  departure_time?: string;
  notes?: string;
}

export interface UpdateAttendanceData {
  status: 'present' | 'absent' | 'late' | 'excused';
  arrival_time?: string;
  departure_time?: string;
  notes?: string;
}

// Get meetings
export const useMeetings = () => {
  return useQuery({
    queryKey: ['meetings'],
    queryFn: async () => {
      const response = await api.get('/meetings');
      return response.data;
    },
  });
};

// Get single meeting
export const useMeeting = (id: number) => {
  return useQuery({
    queryKey: ['meetings', id],
    queryFn: async () => {
      const response = await api.get(`/meetings/${id}`);
      return response.data.data as Meeting;
    },
    enabled: !!id,
  });
};

// Get meeting attendances
export const useMeetingAttendances = (meetingId: number) => {
  return useQuery({
    queryKey: ['meetings', meetingId, 'attendances'],
    queryFn: async () => {
      const response = await api.get(`/meetings/${meetingId}/attendances`);
      return response.data;
    },
    enabled: !!meetingId,
  });
};

// Create meeting
export const useCreateMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMeetingData) => {
      const response = await api.post('/meetings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};

// Update meeting
export const useUpdateMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMeetingData }) => {
      const response = await api.put(`/meetings/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meetings', id] });
    },
  });
};

// Delete meeting
export const useDeleteMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/meetings/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};

// Create attendance
export const useCreateAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAttendanceData) => {
      const response = await api.post('/meetings/attendances', data);
      return response.data;
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['meetings', data.meeting_id, 'attendances'] });
      queryClient.invalidateQueries({ queryKey: ['meetings', data.meeting_id] });
    },
  });
};

// Update attendance
export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateAttendanceData }) => {
      const response = await api.put(`/meetings/attendances/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate all meeting attendances queries since we don't know the meeting_id
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};

// Delete attendance
export const useDeleteAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/meetings/attendances/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
};

// Bulk update attendances
export const useBulkUpdateAttendances = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ meetingId, attendances }: { meetingId: number; attendances: CreateAttendanceData[] }) => {
      const response = await api.put(`/meetings/${meetingId}/attendance`, { attendances });
      return response.data;
    },
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: ['meetings', meetingId, 'attendances'] });
      queryClient.invalidateQueries({ queryKey: ['meetings', meetingId] });
    },
  });
}; 